import React from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';
import { Line } from 'react-chartjs-2';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

interface CashFlowData {
  month: string;
  income: number;
  expenses: number;
  netCashFlow: number;
}

interface CashFlowChartProps {
  data: CashFlowData[];
  isLoading?: boolean;
}

export const CashFlowChart: React.FC<CashFlowChartProps> = ({ data, isLoading = false }) => {
  const chartData = {
    labels: data.map(item => item.month),
    datasets: [
      {
        label: 'Income',
        data: data.map(item => item.income),
        borderColor: '#5ac98c',
        backgroundColor: 'rgba(90, 201, 140, 0.08)',
        fill: true,
        tension: 0.4,
        borderWidth: 1.5,
        pointRadius: 3,
        pointBackgroundColor: '#5ac98c',
        pointBorderColor: '#ffffff',
        pointBorderWidth: 2,
      },
      {
        label: 'Expenses',
        data: data.map(item => item.expenses),
        borderColor: '#ef4444',
        backgroundColor: 'rgba(239, 68, 68, 0.06)',
        fill: true,
        tension: 0.4,
        borderWidth: 1.5,
        pointRadius: 3,
        pointBackgroundColor: '#ef4444',
        pointBorderColor: '#ffffff',
        pointBorderWidth: 2,
      },
      {
        label: 'Net Cash Flow',
        data: data.map(item => item.netCashFlow),
        borderColor: '#d97706',
        backgroundColor: 'rgba(217, 119, 6, 0.08)',
        fill: true,
        tension: 0.4,
        borderWidth: 2,
        pointRadius: 4,
        pointBackgroundColor: '#d97706',
        pointBorderColor: '#ffffff',
        pointBorderWidth: 2,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top' as const,
        align: 'end' as const,
        labels: {
          color: '#6b6a5c',
          usePointStyle: true,
          pointStyle: 'circle',
          padding: 20,
          font: { size: 11, family: "'Inter', sans-serif" },
          boxWidth: 6,
        },
      },
      title: { display: false },
      tooltip: {
        backgroundColor: '#1c2033',
        titleColor: '#f4efe2',
        bodyColor: '#8a8778',
        borderColor: 'rgba(92,78,51,0.2)',
        borderWidth: 1,
        padding: 12,
        titleFont: { size: 12, family: "'Inter', sans-serif", weight: 600 as const },
        bodyFont: { size: 13, family: "'Inter', sans-serif" },
        callbacks: {
          label: function(context: any) {
            const value = new Intl.NumberFormat('en-US', {
              style: 'currency', currency: 'USD', minimumFractionDigits: 0,
            }).format(context.parsed.y);
            return `  ${context.dataset.label}: ${value}`;
          }
        }
      }
    },
    scales: {
      x: {
        grid: { color: 'rgba(92,78,51,0.1)', drawBorder: false },
        border: { display: false },
        ticks: {
          color: '#8a8778',
          font: { size: 11, family: "'Inter', sans-serif" },
          padding: 8,
        },
      },
      y: {
        grid: { color: 'rgba(92,78,51,0.08)', drawBorder: false },
        border: { display: false },
        ticks: {
          color: '#8a8778',
          font: { size: 11, family: "'Inter', sans-serif" },
          padding: 8,
          callback: function(value: any) {
            return new Intl.NumberFormat('en-US', {
              style: 'currency', currency: 'USD', minimumFractionDigits: 0,
              notation: 'compact',
            }).format(value);
          }
        },
      },
    },
    interaction: { intersect: false, mode: 'index' as const },
    animation: { duration: 800 },
  };

  if (isLoading) {
    return (
      <div style={{ background: '#ffffff', border: '1px solid #e3dcc4', borderRadius: 12, padding: 24, boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
        <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 14, fontWeight: 700, color: '#1c2033', marginBottom: 20 }}>
          Cash Flow Trend
        </div>
        <div style={{ height: 260, background: '#faf7ee', borderRadius: 8 }} />
      </div>
    );
  }

  return (
    <div style={{ background: '#ffffff', border: '1px solid #e3dcc4', borderRadius: 12, padding: 24, boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
      {/* Header */}
      <div style={{ marginBottom: 24 }}>
        <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 14, fontWeight: 700, color: '#1c2033' }}>
          Cash Flow Trend
        </div>
        <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 13, color: '#8a8778', marginTop: 3 }}>
          Monthly analysis over the last 6 months
        </div>
      </div>

      <div style={{ height: 260 }}>
        <Line data={chartData} options={options} />
      </div>
    </div>
  );
};
